import { StreemApiAnalyticEventCameraSubjectChangeRequestedToSubject, StreemApiAnalyticEventCameraSubjectSetSubject, StreemApiAnalyticEventConnectionStatusChangedMethod, StreemApiAnalyticEventInviteCreatedMethod, StreemApiAnalyticEventLoginAttemptedMethod, StreemApiAnalyticEventPermissionRequestedPermission, } from '@streem/api';
import Analytics from '../analytics';
import { hasPermission, willPromptForPermission } from './permission_state';
export function recordEvent(event) {
    Analytics.recordEvent(event);
}
// Emitted by both clients when their connection status changes (method or strength)
// Currently, new_strength is modeled by Twilio's NetworkQuality API
// https://www.twilio.com/docs/video/using-network-quality-api
export function recordConnectionStatusChanged(roomSid, newStrength, method) {
    const event = {
        connectionStatusChanged: {
            roomSid,
            newStrength,
            method: StreemApiAnalyticEventConnectionStatusChangedMethod[method],
        },
    };
    recordEvent(event);
}
// Emitted when an expert requests an artifact from the customer
export function recordArtifactRequested(artifactId) {
    const event = {
        artifactRequested: {
            artifact: {
                artifactSid: artifactId,
            },
        },
    };
    recordEvent(event);
}
// Emitted by the tool driver (expert) when they use a tool
// For AR tools, this is when they place the tool in the 3D world
// For Non-AR tools, this is when they complete their interaction (i.e. lift their finger)
// TODO: no 'text' or 'model3d' tools implemented yet (just 'laser', 'draw', 'arrow', and 'measure')
export function recordToolPlaced(eventProperties) {
    const event = {
        toolPlaced: eventProperties,
    };
    recordEvent(event);
}
// Emitted by the tool driver (expert) when they move an AR tool
// TODO: new story (@Adam) -- add analytics to arrow AR tools
export function recordToolMoved(eventProperties) {
    const event = {
        toolMoved: eventProperties,
    };
    recordEvent(event);
}
// Emitted by the tool driver (expert) when they delete an AR tool
export function recordToolRemoved(eventProperties) {
    const event = {
        toolRemoved: eventProperties,
    };
    recordEvent(event);
}
// TODO: cannot implement yet -- 'reject', 'capture', 'fulfilled' are customer-facing and have not yet been addressed in SDK
// Emitted when the customer rejects the expert's artifact request
export function recordArtifactRequestRejected(event) {
    recordEvent(event);
}
// Emitted when a user successfully captures (data on device) an artifact
export function recordArtifactCaptured(event) {
    recordEvent(event);
}
// Emitted when a user successfully fulfills (upload finishes) an artifact
export function recordArtifactFulfilled(artifactId) {
    const event = {
        artifactFulfilled: {
            artifact: {
                artifactSid: artifactId,
            },
        },
    };
    recordEvent(event);
}
// Emitted when a user updates an artifact
// Ex: editing a 3d-streemshot, adding a measurement to a mesh, etc.
export function recordArtifactUpdated(artifactId) {
    const event = {
        artifactUpdated: {
            artifact: {
                artifactSid: artifactId,
            },
        },
    };
    recordEvent(event);
}
// Emitted by both clients when their sent/received resolution changes
// Note: an orientation change is considered a resolution change, even if always-upright frames are sent.
// TODO: cannot yet implement -- we don't keep track of the video resolution yet (customer facing)
export function recordResolutionChanged(roomSid, size) {
    const event = {
        resolutionChanged: {
            roomSid,
            size,
        },
    };
    recordEvent(event);
}
// Emitted when a user attempts to authenticate themselves to Streem through a first-party pipeline
// These methods attempt to exchange user knowledge (user/pass, invitation id, etc.) for a SDK token
export function recordLoginAttempted(method, successful, inviteSid) {
    const event = {
        loginAttempted: {
            method: StreemApiAnalyticEventLoginAttemptedMethod[method],
            successful,
            inviteSid,
        },
    };
    recordEvent(event);
}
// Emitted when a user deletes an artifact
export function recordArtifactDeleted(artifact) {
    const event = {
        artifactDeleted: {
            artifact,
        },
    };
    recordEvent(event);
}
// Emitted when a user views an artifact
// For artifacts like recordings, this is emitted when the user clicks play (not on page load)
export function recordArtifactViewed(artifact) {
    const event = {
        artifactViewed: {
            artifact,
        },
    };
    recordEvent(event);
}
export function recordNoteDeleted() {
    recordEvent({
        noteDeleted: {},
    });
}
export function recordScreenViewed(screenId, roomId) {
    const event = {
        screenViewed: {
            screenId,
            roomSid: roomId?.toString(),
        },
    };
    recordEvent(event);
}
export function recordLoggedOut() {
    const event = {
        loggedOut: {},
    };
    recordEvent(event);
}
// Emitted when a user interacts with an element we want to keep track of interactions with
// Ex: Clicking "Invite to Streem" would emit this event with a unique id
export function recordElementClicked(elementId, roomId) {
    const event = {
        elementClicked: {
            elementId,
            roomSid: roomId?.toString(),
        },
    };
    recordEvent(event);
}
// add roomSid so that it will be treated as a roomEvent
//     This is only available in call details.  To be able to record element scrolled in
//     other contexts, we would need to update snowflake and looker to support the element-scrolled
//     event without sending roomSid.
export function recordElementScrolled(elementId, roomSid) {
    const event = {
        elementScrolled: {
            elementId,
            roomSid,
        },
    };
    recordEvent(event);
}
// Note: element-hovered in not currently used because element-hovered events will not appear
//   in Looker reports.  For it to appear, the following ticket would need to be completed:
// https://ftdr.atlassian.net/browse/ST-8449
//
// eslint-disable-next-line camelcase
export function __DO_NOT_USE_recordElementHovered(elementId) {
    const event = {
        elementHovered: {
            elementId,
        },
    };
    recordEvent(event);
}
// Emmitted when a tooltip we want to keep track of is displayed to a user
// Ex. When a user views more details about their network connectivity score during a call
// by hovering over the network signal level icon
export function recordTooltipViewed(tooltipId) {
    const event = {
        tooltipViewed: {
            tooltipId,
        },
    };
    recordEvent(event);
}
// For web apps, this is when they navigate to another tab or window
export function recordAppBackgrounded() {
    const event = {
        appBackgrounded: {},
    };
    recordEvent(event);
}
// For web apps, this is when they navigate to the tab with Streem
export function recordAppForegrounded() {
    const event = {
        appForegrounded: {},
    };
    recordEvent(event);
}
export function recordNoteSaved(charCount, scopeSid) {
    const event = {
        noteSaved: {
            charCount,
            scopeSid,
        },
    };
    recordEvent(event);
}
// Emitted when a user attempts to exchange a SDK token for an access token (/auth/token)
// The SDK token can be provided by means a Login event or by means of a third-party outside the realm of Streem
// TODO: implement in web/packages/auth/src/managers/access_token_manager.ts
export function recordIdentifyAttempted(successful, groupReservationSid) {
    const event = {
        identifyAttempted: {
            successful,
            groupReservationSid,
        },
    };
    recordEvent(event);
}
// Emitted when a user attempts to invite another user to a Streem by creating an invitation
export function recordInviteCreated(inviteSid, method) {
    const event = {
        inviteCreated: {
            inviteSid,
            method: StreemApiAnalyticEventInviteCreatedMethod[method],
        },
    };
    recordEvent(event);
}
export async function attemptPermissionRequest(permission, request, onRequestError) {
    const usePermissionsApi = 'permissions' in navigator;
    const { result, requested, granted } = usePermissionsApi
        ? await attemptPermissionApiRequest(permission, request, onRequestError)
        : await attemptPermissionTimingRequest(request, onRequestError);
    recordPermissionRequested(permission, granted, !!requested);
    return result;
}
/**
 * Use Permissions API for exact results on if permission was requested or not.
 * @see {@link https://caniuse.com/permissions-api} for current browser support
 */
async function attemptPermissionApiRequest(permission, request, onRequestError) {
    let result;
    let error;
    let requested;
    try {
        requested = await willPromptForPermission(permission);
    }
    catch (e) {
        requested = false;
        error = e;
    }
    // Perform the actual request
    try {
        result = await request();
    }
    catch (e) {
        onRequestError(e);
    }
    // In certain situations, the browser will report that the permission has been granted when it actually hasn't
    // Ex: the browser has been denied permissions from the OS
    let granted;
    if (error instanceof Error && error?.message.includes('Permission denied')) {
        granted = false;
    }
    else {
        try {
            granted = await hasPermission(permission);
        }
        catch (e) {
            granted = false;
        }
    }
    return {
        result,
        requested,
        granted,
    };
}
/**
 * Use timings and errors to determine whether or not permission was requested or not
 */
async function attemptPermissionTimingRequest(request, onRequestError) {
    let result;
    let requested;
    let granted;
    const startTime = new Date().getTime();
    try {
        result = await request();
        granted = true;
    }
    catch (e) {
        onRequestError(e);
        granted = false;
    }
    const endTime = new Date().getTime();
    if (endTime - startTime > 1000) {
        requested = true;
    }
    return {
        result,
        requested,
        granted,
    };
}
export function recordPermissionRequested(permission, granted, promptShown) {
    const event = {
        permissionRequested: {
            permission: StreemApiAnalyticEventPermissionRequestedPermission[permission],
            inviteSid: Analytics._inviteId?.toString(),
            groupReservationSid: Analytics._groupReservationId?.toString(),
            granted,
            promptShown,
        },
    };
    recordEvent(event);
}
export function recordWebRTCStats(roomSid, { localVideo = [], localAudio = [], remoteVideo = [], remoteAudio = [] }) {
    localVideo.forEach(stats => {
        stats.roomSid = roomSid;
        recordEvent({
            localVideoStatsMeasured: stats,
        });
    });
    localAudio.forEach(stats => {
        stats.roomSid = roomSid;
        recordEvent({
            localAudioStatsMeasured: stats,
        });
    });
    remoteVideo.forEach(stats => {
        stats.roomSid = roomSid;
        recordEvent({
            remoteVideoStatsMeasured: stats,
        });
    });
    remoteAudio.forEach(stats => {
        stats.roomSid = roomSid;
        recordEvent({
            remoteAudioStatsMeasured: stats,
        });
    });
}
export function recordCameraStatusSet(enabled, roomId) {
    const event = {
        cameraStatusSet: {
            roomSid: roomId.toString(),
            enabled,
        },
    };
    recordEvent(event);
}
export function recordMicrophoneStatusSet(enabled, roomId) {
    const event = {
        microphoneStatusSet: {
            roomSid: roomId.toString(),
            enabled,
        },
    };
    recordEvent(event);
}
export function recordCameraSubjectChangeRequested(roomId, toSubject) {
    const event = {
        cameraSubjectChangeRequested: {
            roomSid: roomId.toString(),
            toSubject: StreemApiAnalyticEventCameraSubjectChangeRequestedToSubject[toSubject],
        },
    };
    recordEvent(event);
}
export function recordCameraSubjectChangeRejected(roomId) {
    const event = {
        cameraSubjectChangeRejected: {
            roomSid: roomId.toString(),
        },
    };
    recordEvent(event);
}
export function recordCameraSubjectChangeAborted(roomId) {
    const event = {
        cameraSubjectChangeAborted: {
            roomSid: roomId.toString(),
        },
    };
    recordEvent(event);
}
export function recordCameraSubjectChanged(roomId, subject) {
    const event = {
        cameraSubjectSet: {
            roomSid: roomId.toString(),
            subject: StreemApiAnalyticEventCameraSubjectSetSubject[subject],
        },
    };
    recordEvent(event);
}
//# sourceMappingURL=event_helpers.js.map